import { useState } from "react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkle, ArrowClockwise, TextAa } from "@phosphor-icons/react";

const TONES = [
  { v: "professionnel", label: "Professionnel" },
  { v: "chaleureux", label: "Chaleureux" },
  { v: "direct", label: "Direct" },
  { v: "formel", label: "Formel" },
];
const LENGTHS = [
  { v: "court", label: "Court" },
  { v: "moyen", label: "Moyen" },
  { v: "long", label: "Long" },
];

/**
 * Assistant de rédaction IA réutilisable (façon Shortwave/Spacemail).
 * Props:
 *  - run(payload) => Promise<{variants:[{subject, body_html}], generated}>
 *  - getCurrent() => {subject, body_html} (pour reformuler)
 *  - onApply({subject, body_html})
 *  - defaultVariants (nombre de variantes à générer, défaut 2)
 *  - testid
 */
export function AiComposer({ run, getCurrent, onApply, defaultVariants = 2, testid = "ai-composer" }) {
  const [tone, setTone] = useState("professionnel");
  const [length, setLength] = useState("moyen");
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState("");
  const [variants, setVariants] = useState([]);
  const [idx, setIdx] = useState(0);

  const call = async (action, overrides = {}, tag = "") => {
    setBusy(tag || action);
    try {
      const current = action === "refine" ? getCurrent?.() : null;
      const data = await run({
        action, tone, length, instructions: instr,
        variants: action === "refine" ? 1 : defaultVariants,
        current, ...overrides,
      });
      const vs = data?.variants || [];
      if (!vs.length) { toast.error("Aucun brouillon généré, réessayez."); return; }
      setVariants(vs); setIdx(0);
      onApply(vs[0]);
      toast.success(
        action === "refine" ? "Brouillon retravaillé ✨"
          : (data.generated ? "Rédigé par l'IA ✨" : "Brouillon inséré"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "L'IA n'a pas pu répondre.");
    } finally { setBusy(""); }
  };

  const pickVariant = (i) => { setIdx(i); onApply(variants[i]); };

  const chip = (label, onClick, key) => (
    <button key={key} type="button" onClick={onClick} disabled={!!busy}
      data-testid={`${testid}-quick-${key}`}
      className="text-[11px] px-2 py-1 rounded-full border border-border bg-surface hover:bg-surface-2 text-muted hover:text-fg disabled:opacity-50">
      {busy === key ? "…" : label}
    </button>
  );

  return (
    <div className="rounded-lg border border-accent/30 bg-accent/[0.04] p-2.5 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-accent">
        <Sparkle size={14} weight="fill" /> Assistant de rédaction IA
      </div>

      {/* Ton + longueur */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={tone} onValueChange={setTone}>
          <SelectTrigger className="h-8 w-40 bg-surface border-border text-xs" data-testid={`${testid}-tone`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TONES.map((t) => <SelectItem key={t.v} value={t.v}>Ton : {t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid={`${testid}-length`}>
          {LENGTHS.map((l) => (
            <button key={l.v} type="button" onClick={() => setLength(l.v)}
              data-testid={`${testid}-length-${l.v}`}
              className={`text-[11px] px-2.5 py-1.5 ${length === l.v ? "bg-accent/20 text-accent font-semibold" : "bg-surface text-muted hover:text-fg"}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Consigne libre */}
      <div className="flex items-center gap-2">
        <Input value={instr} onChange={(e) => setInstr(e.target.value)}
          placeholder="Consigne (ex. insiste sur l'expérience retail, ajoute une accroche…)"
          className="h-8 bg-surface border-border text-xs" data-testid={`${testid}-instructions`}
          onKeyDown={(e) => { if (e.key === "Enter") call("generate"); }} />
      </div>

      {/* Actions principales */}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => call("generate")} disabled={!!busy}
          data-testid={`${testid}-generate`}
          className="flex items-center gap-1.5 text-xs font-semibold bg-accent text-white rounded-md px-3 py-1.5 hover:opacity-90 disabled:opacity-60">
          <Sparkle size={13} weight="fill" /> {busy === "generate" ? "Rédaction…" : "Générer"}
        </button>
        <button type="button" onClick={() => call("refine", {}, "refine")} disabled={!!busy}
          data-testid={`${testid}-refine`}
          className="flex items-center gap-1.5 text-xs font-semibold bg-surface border border-border rounded-md px-3 py-1.5 hover:bg-surface-2 disabled:opacity-60">
          <ArrowClockwise size={13} /> {busy === "refine" ? "…" : "Reformuler"}
        </button>
        <span className="w-px h-4 bg-border mx-0.5" />
        {chip("Plus court", () => call("refine", { length: "court" }), "shorter")}
        {chip("Plus long", () => call("refine", { length: "long" }), "longer")}
        {chip("Plus formel", () => call("refine", { tone: "formel" }), "formal")}
        {chip("Plus direct", () => call("refine", { tone: "direct" }), "direct")}
      </div>

      {/* Sélecteur de variantes */}
      {variants.length > 1 && (
        <div className="flex items-center gap-1.5 pt-1" data-testid={`${testid}-variants`}>
          <TextAa size={13} className="text-muted" />
          <span className="text-[11px] text-muted">Variantes :</span>
          {variants.map((_, i) => (
            <button key={i} type="button" onClick={() => pickVariant(i)}
              data-testid={`${testid}-variant-${i}`}
              className={`text-[11px] px-2 py-1 rounded-md border ${idx === i ? "border-accent bg-accent/15 text-accent font-semibold" : "border-border bg-surface text-muted hover:text-fg"}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
